import { toast } from "react-toastify"

import dayjs from "libs/dayjs"

import { ReactComponent as CopyIcon } from "../assets/icons/copy-icon.svg"
import { GameStatusType, TableDataType } from "../constants/eums"
import { GameSessions } from "../services/user/type"

export const formatDate = (date: string | Date) => {
  if (!date) return "-"
  return dayjs(date).format("DD.MM.YYYY")
}
export const formatDateTime = (date: string | Date) => {
  return dayjs(date).format("DD.MM.YYYY h:mm:ss A Z")
}

export const formatBoolean = (value?: boolean) => {
  return value ? "YES" : "NO"
}

/**
 * Helper function to format historical USD values ($0 or $100.00 format is returned from BE)
 * @param value
 */
/**
 * Placeholder shown for a balance/financial field the backend reported as
 * temporarily unavailable (microservice mode, FS/bonus-service down). Use this
 * instead of "$0.00" — a false zero reads as "the money is gone".
 */
export const UNAVAILABLE_DASH = "—"

export const parseAndFormatHistoricalUSD = (value: string) => {
  const num = parseFloat(value.replace(/[$,]/g, ""))
  if (isNaN(num)) return value
  return formatFiatCurrency(num * 100, "$0.00")
}

// B_USD is the USD-denominated bonus coin.
const toIntlCurrency = (currency: string) => {
  if (currency === "B_USD") {
    return "USD"
  }
  return currency
}

export const formatFiatCurrency = (value?: number | string, placeholder = "", currency = "USD") => {
  if (!value) return placeholder

  const amount = Number(value) / 100

  // Codes Intl rejects render as plain text instead of throwing.
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: toIntlCurrency(currency),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

/**
 * Currency symbols for the tournament display currency (label only).
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  CAD: "C$",
  EUR: "€",
  JPY: "¥",
  PLN: "zł",
  BRL: "R$",
  NOK: "kr",
  SEK: "kr",
  TRY: "₺",
  RUB: "₽",
  PHP: "₱",
  DKK: "kr",
  AUD: "A$",
  NGN: "₦",
  KES: "KSh",
  TZS: "TSh",
  UGX: "USh",
  CLP: "CLP$",
  MXN: "MX$"
}

export const getCurrencySymbol = (currency?: string): string => (currency && CURRENCY_SYMBOLS[currency]) || "$"

/**
 * Fiat vs crypto classification for a transaction's `coin`, mirroring core-api's FIAT_COINS
 * (utils/payment/currency-resolvers.ts) — anything not a tracked fiat cash currency is crypto.
 */
export const isFiatCoin = (coin?: string | null): boolean => !!coin && coin.toUpperCase() in CURRENCY_SYMBOLS

/**
 * Format a native minor-unit value (already in the tournament currency) with its symbol.
 */
export const formatTournamentMoney = (minorUnits?: number | string | null, currency?: string, digits = 2): string => {
  const amount = Number(minorUnits || 0) / 100
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(amount)
  return `${getCurrencySymbol(currency)}${formatted}`
}

/** Convert a USD-cents value to the tournament currency's minor units via the frozen rate. */
export const usdCentsToCurrencyMinor = (usdCents?: number | string | null, rate?: number | null): number =>
  Math.round((Number(usdCents) || 0) * (rate ?? 1))

/**
 * Resolve a configured tournament money field to native minor units: prefer the exact
 * admin-entered value in `display_amounts`, else convert the USD column with the frozen rate.
 */
export const tournamentDisplayMinor = (
  t: { display_amounts?: Record<string, any> | null; currency_rate?: number | null } | null | undefined,
  field: string,
  usdCents?: number | string | null
): number => {
  const native = t?.display_amounts?.[field]
  if (native !== null && native !== undefined) return Number(native)
  return usdCentsToCurrencyMinor(usdCents, t?.currency_rate)
}

export const formatNumber = (value?: number | string, placeholder = "") => {
  if (!value && value !== 0) return placeholder

  const amount = Number(value)

  return new Intl.NumberFormat("en-US").format(amount)
}

export const formatCryptoCurrency = (value?: number | string) => {
  if (!value) return ""

  let amount = Number(value)
  // if (amount < 0) amount *= -1

  return (amount / 1e8).toString()
}

export const formatPercentage = (value?: number | string) => {
  if (!value) return ""
  return `${value} %`
}

export const formatTableCell = (value: any, type: TableDataType) => {
  switch (type) {
    case TableDataType.DATE:
      return formatDate(value)
    case TableDataType.BOOLEAN:
      return formatBoolean(value)
    case TableDataType.FIAT_CURRENCY:
      return formatFiatCurrency(value, "$0.00")
    case TableDataType.TFIAT_CURRENCY:
      return formatFiatCurrency(value, "-")
    case TableDataType.CRYPTO_CURRENCY:
      return formatCryptoCurrency(value)
    case TableDataType.PERCENTAGE:
      return formatPercentage(value)
    case TableDataType.SHORTENED_STRING:
      return (
        <button
          className="text-blue-500 text-sm flex justify-between w-full gap-2 underline min-w-[150px] hover:text-blue-700"
          onClick={(e) => {
            e.stopPropagation()
            navigator.clipboard.writeText(value)
            toast.success("Copied to clipboard!")
          }}
          title="Click to copy full value"
        >
          {shortenString(value)}
          <CopyIcon className="stroke-blue-500" />
        </button>
      )

    default:
      return value
  }
}

export const formatTableCellForPrint = (value: any, type: TableDataType) => {
  switch (type) {
    case TableDataType.DATE:
      return formatDate(value)
    case TableDataType.BOOLEAN:
      return formatBoolean(value)
    case TableDataType.FIAT_CURRENCY:
      return formatFiatCurrency(value)
    case TableDataType.TFIAT_CURRENCY:
      return formatFiatCurrency(value, "-")
    case TableDataType.CRYPTO_CURRENCY:
      return formatCryptoCurrency(value)
    case TableDataType.PERCENTAGE:
      return formatPercentage(value)
    case TableDataType.JSON:
      return value.map((item: GameSessions) => `${item.currency} - ${item.short_id}`).join(", ")
    default:
      return value
  }
}

export const shortenString = (value: string, startLength: number = 6, endLength: number = 4) => {
  if (!value) return ""
  return value.length > startLength + endLength ? `${value.slice(0, startLength)}...${value.slice(-endLength)}` : value
}

export function formatRewardGameStatus(rewardGame: any): GameStatusType {
  if (rewardGame?.issue_id) {
    switch (rewardGame?.status) {
      case "active":
        return GameStatusType.Play
      case null:
        return GameStatusType.Pending
      default:
        return GameStatusType.Claimed
    }
  }

  return GameStatusType.Claim
}

export const formatDateRange = (fromDate?: Date | null, untilDate?: Date | null) => {
  let formattedRange = ""
  if (fromDate) {
    if (fromDate.getTime() === 0) {
      formattedRange += "- "
    } else {
      formattedRange += `${formatDate(fromDate)} - `
    }
  }
  if (untilDate) {
    if (untilDate.getTime() >= 0) {
      formattedRange += `${formatDate(untilDate)}`
    }
  }
  return formattedRange
}

export const formatWordToCapitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()

export const centsToUsdInput = (cents: number) => (cents / 100).toFixed(2)

export const parseNonNegInt = (v: string) => {
  if (v.trim() === "") return null
  const n = Number(v)
  if (!Number.isFinite(n)) return NaN
  return n
}

export const parseIntSafe = (v: string) => {
  if (!v.trim()) return null
  const n = Number(v)
  return Number.isFinite(n) ? Math.floor(n) : null
}

export const parseUsdToCents = (v: string) => {
  if (v.trim() === "") return null
  const n = Number(v)
  if (!Number.isFinite(n)) return NaN
  return Math.round(n * 100)
}

export const isValidPercent = (v: unknown) => {
  const n = Number(v)
  return !Number.isNaN(n) && n >= 0 && n <= 100
}

export const keyFor = (x: number) => String(x)

export const getMonthYear = (date: Date | null): { month: number; year: number } | null => {
  if (!date) return null
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1
  }
}
